import {
  useLocation,
  useNavigate,
  type NavigateOptions,
} from "react-router-dom";

interface LocationState {
  from?:
    | {
        pathname: string;
      }
    | string;
}

interface UseReturnToReturn {
  returnToPath: string;
  returnTo: (options?: NavigateOptions) => void;
}

// Helper to check if a string is an absolute URL with a protocol or protocol-relative
const isFullUrl = (url: string): boolean => {
  try {
    // Matches http://, https://, //, etc.
    return /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i.test(url);
  } catch {
    return false;
  }
};

export default function useReturnTo(
  defaultPath: string = "/",
): UseReturnToReturn {
  const { search, state } = useLocation();
  const navigate = useNavigate();

  const getReturnToPath = (): string => {
    // 1. Check URL query parameters e.g., ?redirectTo=/dashboard or ?redirectTo=https://example.com
    const params = new URLSearchParams(search);
    const redirectTo = params.get("redirectTo") || params.get("returnTo");

    if (redirectTo) {
      // Allow relative paths starting with single '/' OR full URLs
      if (
        (redirectTo.startsWith("/") && !redirectTo.startsWith("//")) ||
        isFullUrl(redirectTo)
      ) {
        return redirectTo;
      }
    }

    // 2. Check router state location (e.g., state?.from)
    const typedState = state as LocationState | null;
    if (typedState?.from) {
      const fromPath =
        typeof typedState.from === "object"
          ? typedState.from.pathname
          : typedState.from;
      if (
        (fromPath.startsWith("/") && !fromPath.startsWith("//")) ||
        isFullUrl(fromPath)
      ) {
        return fromPath;
      }
    }

    // 3. Fallback path
    return defaultPath;
  };

  const returnTo = (options: NavigateOptions = { replace: true }): void => {
    const path = getReturnToPath();

    if (isFullUrl(path)) {
      if (options.replace) {
        window.location.replace(path);
      } else {
        window.location.href = path;
      }
    } else {
      navigate(path, options);
    }
  };

  return {
    returnToPath: getReturnToPath(),
    returnTo,
  };
}
